// What the QR code on a stand encodes, and what a page does with it.
//
// THE ANSWER TO "WHAT THE QR ENCODES", AND THE ARGUMENT FOR IT.
//
// It encodes a URL naming an activation, and nothing else. No token, no
// signature, no expiry, no per-stand secret. `https://<host>/j/<activation_id>`,
// where the id is the uuid already in `activation` and already on the printed
// sheet a crew member is handed.
//
// THE OBVIOUS OBJECTION IS THE RIGHT ONE AND IT IS ANSWERED RATHER THAN DENIED:
// yes, this can be photographed off the stand and used from anywhere, forever.
// That is acceptable, and it is acceptable because the QR code was never the
// control. A stand's QR code is on a wall in a hall with thirty thousand people
// in it, photographable from ten metres by anybody walking past, and reproduced
// in the client's own social media before lunch. Any secret printed on it is a
// secret in a photograph. The emailed verification code is the control, and it
// works precisely because it is *not* on the wall.
//
// WHAT A TOKEN IN THE QR WOULD HAVE BOUGHT, since it is the design everybody
// reaches for first and it is worth writing down why it was refused.
//
//   A short-lived signed token would mean reprinting the stand every few hours,
//   which nobody will do, so in practice it becomes a long-lived one — and a
//   long-lived signed token is the string above with extra machinery and a
//   rotation nobody schedules.
//
//   A per-stand secret would let the server tell two stands at one show apart.
//   The activation id already does that, without being a secret.
//
//   A token would create a second credential to leak, on the surface with the
//   worst leak properties in the system: the string is rendered as a picture,
//   which people photograph and post.
//
//   And crucially, it would defend against nothing the emailed code does not
//   already defend against better. A scraper with the token still has to receive
//   mail at a distinct deliverable address to produce a single registration.
//
// WHAT THE ACTIVATION ID IN THE LINK THEREFORE AUTHORISES: nothing. It names
// which stand somebody is at. Every ceiling that matters is server-side and
// keyed on that name — see the three activation-scoped rules in
// api/src/rateLimit.ts — and every write is gated by a mailbox the server sent
// something to. Read `activation_id` as an address, not as a permission, and
// the rest of this package makes sense.
//
// A CONSEQUENCE WORTH STATING BECAUSE SOMEBODY WILL ASK. Anybody can open a
// stand's registration page from a sofa. They can, and the page has nothing to
// hide: it shows the client's name, the fields, the consents and the notices. If
// that set is ever considered confidential, the answer is not a token in the
// QR — it is that the page must not be public, which is a different product.
//
// ---------------------------------------------------------------------------
//
// ON THE PATH BEING `/j/` AND NOT `/join/` OR `/register/`. The link is printed
// under a QR code at a size a person may have to type it, and every character
// is one somebody gets wrong on a phone keyboard in a noisy hall. Short also
// keeps the QR's module count down, which is the difference between a code that
// scans at arm's length and one that does not.
//
// ON THERE BEING NO QUERY STRING. A path segment rather than `?a=<id>` because a
// query string is what gets stripped by link shorteners, mangled by scanner apps
// that "clean" URLs, and dropped by a captive portal's redirect. A path survives
// more of those. It also means the whole link is one token to a QR encoder,
// which keeps it in a smaller symbol.

/**
 * The one path shape this surface answers on.
 *
 * Exported as the literal prefix rather than assembled at each call site, so
 * that the router, the link builder and the check all agree by construction.
 */
export const JOIN_PATH_PREFIX = "/j/";

/**
 * The onsite registration path on the same host. Not what the public QR encodes.
 *
 * A photographed QR is the public address. This prefix is a different URL, and
 * it is the credential for a hall browser: no mailbox, onsite_web channel.
 * This path always ends at registration. The onsite wheel has its own `/sw/`
 * path so both can run at the same activation.
 */
export const ONSITE_PATH_PREFIX = "/s/";

/** The onsite wheel: no mailbox, then details, terms, survey and a spin. */
export const ONSITE_WHEEL_PATH_PREFIX = "/sw/";

/**
 * The prize-wheel public path on the same host.
 *
 * LIVE AT THE SAME TIME AS `/j/`, NOT INSTEAD OF IT. Both write a registration
 * through the attend routes; this one ends in a spin and the other does not, so
 * a stand can put the plain link on a badge scanner and the wheel link under
 * the QR beside the dial, and both work. Join kind picks which of the two the
 * console prints as the stand's main code; it no longer turns the other one
 * off. Hall tablets use `/s/` for registration and `/sw/` for the wheel.
 */
export const WHEEL_PATH_PREFIX = "/w/";

/**
 * Canonical, lowercase, hyphenated, and nothing else.
 *
 * Deliberately not `v.uuid()`'s test from the schema package: this module is
 * imported by the browser bundle and by a build script, and the schema
 * validators are a dependency neither of those needs. The two are compared in
 * check.mjs against the same set of strings, which is what keeps them the same
 * test rather than two tests that agree today.
 */
const AN_ACTIVATION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export type LinkReading =
  | { readonly ok: true; readonly activationId: string }
  /**
   * A refusal a person standing at a stand can act on.
   *
   * `problem` is what the page shows them and `code` is what a log line and a
   * check can compare, on the rule the rest of XP1 holds: refusals are named,
   * never generic, and they say what to do next. The thing to do next is nearly
   * always "ask somebody on the stand", because that is a real option here in a
   * way it is not on a kiosk.
   */
  | { readonly ok: false; readonly code: LinkRefusal; readonly problem: string };

export type LinkRefusal = "no_activation_in_link" | "not_an_activation_id";

/**
 * Read the activation out of a path.
 *
 * Takes the path rather than reading `location`, so that the router, the check
 * and any future deep link all drive one function over inputs they choose. A
 * module that read `location` directly could not be checked without a DOM, and
 * a rule that can only be exercised through a DOM is a rule that ends up
 * exercised through none.
 *
 * THE UPPERCASE CASE IS ACCEPTED AND NORMALISED, WHICH IS NOT LAXNESS. QR
 * encoders routinely emit uppercase because alphanumeric mode is denser than
 * byte mode and produces a smaller symbol, and a well-meaning person generating
 * a replacement code with an online tool will get one. Refusing it would present
 * as "the QR code on the stand does not work" with no way to tell that the id in
 * it is correct. The value is lowercased before it goes anywhere, because 114's
 * column is a uuid and the API compares it as one.
 */
function readPrefixedLink(
  pathname: string,
  prefix: string,
  missing: string,
  malformed: string,
): LinkReading {
  if (!pathname.startsWith(prefix)) {
    return { ok: false, code: "no_activation_in_link", problem: missing };
  }

  const rest = pathname.slice(prefix.length).replace(/\/$/, "");
  const candidate = rest.toLowerCase();

  if (!AN_ACTIVATION_ID.test(candidate)) {
    return { ok: false, code: "not_an_activation_id", problem: malformed };
  }

  return { ok: true, activationId: candidate };
}

export function readJoinLink(pathname: string): LinkReading {
  return readPrefixedLink(
    pathname,
    JOIN_PATH_PREFIX,
    "This link does not name a stand. Scan the QR code on the stand again, or ask somebody on the " +
      "stand to register you.",
    "This link does not name a stand this page recognises. Scan the QR code on the stand again, or " +
      "ask somebody on the stand to register you.",
  );
}

export function readOnsiteLink(pathname: string): LinkReading {
  return readPrefixedLink(
    pathname,
    ONSITE_PATH_PREFIX,
    "This link does not name an onsite stand. Open the onsite address from the console, or use the tablet.",
    "This link does not name a stand this page recognises. Open the onsite address from the console, or " +
      "use the tablet.",
  );
}

export function readOnsiteWheelLink(pathname: string): LinkReading {
  return readPrefixedLink(
    pathname,
    ONSITE_WHEEL_PATH_PREFIX,
    "This link does not name an onsite wheel. Open the onsite wheel address from the console.",
    "This link does not name a wheel this page recognises. Open the onsite wheel address from the console.",
  );
}

export function readWheelLink(pathname: string): LinkReading {
  return readPrefixedLink(
    pathname,
    WHEEL_PATH_PREFIX,
    "This link does not name a stand. Scan the QR code on the stand again, or ask somebody on the " +
      "stand to register you.",
    "This link does not name a stand this page recognises. Scan the QR code on the stand again, or " +
      "ask somebody on the stand to register you.",
  );
}

/**
 * The link to print under a QR code.
 *
 * Here rather than in whatever produces the printed sheet, so that the string
 * that gets encoded and the string that gets parsed are produced by one module.
 * The alternative is a print template with the path typed into it, which agrees
 * with this file right up until somebody renames the route.
 *
 * `origin` is passed in rather than read, because the origin is a hosting
 * decision that is not settled — see xp/WEB-REPORT.md on where this surface
 * belongs — and a default here would be the thing that ships.
 */
export function joinLink(origin: string, activationId: string): string {
  const id = activationId.trim().toLowerCase();
  if (!AN_ACTIVATION_ID.test(id)) {
    throw new Error(
      `joinLink was given ${JSON.stringify(activationId)}, which is not an activation id. A QR code ` +
        "printed from a bad id is a stand that cannot take a registration for the length of a show, and " +
        "nobody finds out until an attendee tries.",
    );
  }
  return `${origin.replace(/\/$/, "")}${JOIN_PATH_PREFIX}${id}`;
}

/**
 * The onsite address. Never encoded in the public QR.
 */
export function standLink(origin: string, activationId: string): string {
  const id = activationId.trim().toLowerCase();
  if (!AN_ACTIVATION_ID.test(id)) {
    throw new Error(
      `standLink was given ${JSON.stringify(activationId)}, which is not an activation id. An onsite ` +
        "address printed from a bad id is a hall page that cannot take a registration.",
    );
  }
  return `${origin.replace(/\/$/, "")}${ONSITE_PATH_PREFIX}${id}`;
}

/** The onsite wheel address. Never encoded in the public QR. */
export function standWheelLink(origin: string, activationId: string): string {
  const id = activationId.trim().toLowerCase();
  if (!AN_ACTIVATION_ID.test(id)) {
    throw new Error(
      `standWheelLink was given ${JSON.stringify(activationId)}, which is not an activation id. An ` +
        "onsite wheel address printed from a bad id is a hall page that cannot run.",
    );
  }
  return `${origin.replace(/\/$/, "")}${ONSITE_WHEEL_PATH_PREFIX}${id}`;
}

/**
 * The prize-wheel public address. Encoded in the printed QR only when the
 * Console Wheel desk has saved this stand as a wheel.
 */
export function wheelLink(origin: string, activationId: string): string {
  const id = activationId.trim().toLowerCase();
  if (!AN_ACTIVATION_ID.test(id)) {
    throw new Error(
      `wheelLink was given ${JSON.stringify(activationId)}, which is not an activation id. A QR code ` +
        "printed from a bad id is a stand that cannot take a registration for the length of a show, and " +
        "nobody finds out until an attendee tries.",
    );
  }
  return `${origin.replace(/\/$/, "")}${WHEEL_PATH_PREFIX}${id}`;
}

export function publicJoinPath(
  activationId: string,
  joinKind: "registration" | "wheel",
): string {
  const id = activationId.trim().toLowerCase();
  return `${joinKind === "wheel" ? WHEEL_PATH_PREFIX : JOIN_PATH_PREFIX}${id}`;
}

/**
 * Put the address bar back on the path this page is actually running.
 *
 * WHAT THIS USED TO DO AND WHY IT STOPPED. It rewrote `/j/` to `/w/`, or back,
 * to match the stand's join kind, on the reading that a stand ran one of the
 * two. That reading is gone: a stand runs both at once, `/j/` is the plain
 * registration and `/w/` is the same registration with a spin at the end, and
 * which one somebody is on is a fact about the QR they scanned. Rewriting it
 * took a crew member who had deliberately copied the `/j/` link and moved them
 * to the wheel, which is the bug this rewrite now exists to not cause.
 *
 * What is left is narrower and still worth having: a magic link arrives as
 * `/w/<id>/v/<token>`, the token is stripped in place, and an id typed in
 * uppercase off a printed sheet should settle to the canonical lowercase. Both
 * keep the prefix they came in on.
 */
export function syncPublicJoinPath(
  location: Location,
  history: History,
  activationId: string,
  join: "registration" | "wheel",
): void {
  const wanted = publicJoinPath(activationId, join);
  if (location.pathname !== wanted) {
    history.replaceState(null, "", `${wanted}${location.search}${location.hash}`);
  }
}
